import type {
  Cart,
  CountryCode,
  MailingAddressInput,
  Maybe,
} from "@shopify/hydrogen-react/storefront-api-types";

import {
  getDomainInfo,
  TUNE_ARCHIVE_SALE_CLOUDFRONT_DOMAIN,
  TUNE_ARCHIVE_SALE_DOMAIN,
  TUNE_PREVIEW_DOMAIN,
  TUNE_PROD_DOMAIN,
} from "../const/url";

export type TShippingLine =
  | { shippingRateHandle: string }
  | {
      title?: string;
      priceWithCurrency?: {
        amount: number;
        currencyCode: string;
      };
      shippingRateHandle?: string;
    };

export interface IPaymentParam {
  orderData: {
    purchasingEntity: { customerId: string };
    lineItems: {
      quantity: number;
      variantId: string;
      appliedDiscount?: {
        description: string;
        valueType: string;
        value: number;
      };
    }[];
    shippingAddress?: MailingAddressInput & {
      countryCode?: string | Maybe<CountryCode>;
      provinceCode?: string;
    };
    billingAddress?: MailingAddressInput & {
      countryCode?: string | Maybe<CountryCode>;
      provinceCode?: string;
    };
    customAttributes?: { key: string; value: string }[];
    shippingLine: TShippingLine;
    acceptAutomaticDiscounts: boolean;
    discountCodes?: string[];
    localizedFields?: { key: string; value: string }[];
    tags?: string[];
    appliedDiscount?: {
      description: string;
      valueType: string;
      value: number;
    };
  };
}

export interface ICreateDraftOrder {
  orderId: string;
  orderNumber: string;
  order?: IShopifyOrder;
  message?: string;
}

export interface IShopifyOrder {
  id: string;
  name: string;
  processedAt: string;
  totalPriceSet: IShopMoney;
  lineItems: {
    edges: {
      node: ILineItem;
    }[];
  };
}

export interface ILineItem {
  title: string;
  quantity: number;
  originalTotalSet: IShopMoney;
  discountedTotalSet: IShopMoney;
}

interface IShopMoney {
  amount: string;
}

const KR_API_URL = {
  thisisneverthat:
    "https://dycbkwm4xaqdzyb3ebq4onnqqu0cvcxb.lambda-url.ap-northeast-2.on.aws/",
  yeseyesee:
    "https://xe4cpjkt23o6h33gblqgm23jry0bzdpi.lambda-url.ap-northeast-2.on.aws/",
  khakis:
    "https://slrbjbhohxpahm3qnhkfhuwigm0cfxii.lambda-url.ap-northeast-2.on.aws/",
  tune: `${TUNE_PROD_DOMAIN}api/order/create`, // https://w5345hyzyumpvcpvvo4rtkt2hy0eulsx.lambda-url.ap-northeast-2.on.aws/",
  sunlove:
    "https://ardejr3kdpsid2ziwmvzlrhfe40jhfox.lambda-url.ap-northeast-2.on.aws/",
  wellbeingexpress:
    "https://kmexnw5mhbpbrxfr27vfc6vxsy0nfktm.lambda-url.ap-northeast-2.on.aws/",
};

const INTL_API_URL = {
  thisisneverthat:
    "https://ccazobvdblbbieaobs6hbo67ue0uzwsm.lambda-url.ap-northeast-2.on.aws/ ",
  yeseyesee:
    "https://3mqdb2gvqk6ccm53vldx3jb2ue0sodaa.lambda-url.ap-northeast-2.on.aws/",
  tune: "",
  khakis:
    "https://jecqa33s6w2qi375wuxz2saoai0ydgmx.lambda-url.ap-northeast-2.on.aws/",
  sunlove:
    "https://ovjqd3e26cekkyzi2riy43ucfm0qlhvy.lambda-url.ap-northeast-2.on.aws/",
  wellbeingexpress: "",
};

export const createDraftOrder = async ({
  shop,
  domain,
  param,
  useTestClientKey = false,
}: {
  shop: string;
  domain: "ko" | "en" | "jp";
  param: IPaymentParam & {
    paymentKey: string;
    orderId: string;
    amount: number;
    brandPay?: string;
    cartId?: string;
    cart?: Cart;
  };

  useTestClientKey?: boolean; // toss 테스트 키 사용 여부 true이면 테스트 키 사용, false이면 고유 키 사용
}): Promise<
  | {
      status: number;
      data: ICreateDraftOrder;
    }
  | undefined
> => {
  const apiUrl = domain === "ko" ? KR_API_URL : INTL_API_URL;
  const isTunePreview =
    shop === "tune" &&
    typeof window !== "undefined" &&
    getDomainInfo("tune", window.location.href).isPreview;

  const isTuneArchiveSale =
    shop === "tune" &&
    typeof window !== "undefined" &&
    (window.location.href.includes(TUNE_ARCHIVE_SALE_DOMAIN) ||
      window.location.href.includes(TUNE_ARCHIVE_SALE_CLOUDFRONT_DOMAIN));

  const getLambdaUrl = (): string => {
    if (isTunePreview) return `${TUNE_PREVIEW_DOMAIN}api/order/create`;
    if (isTuneArchiveSale) return `${window.location.origin}/api/order/create`;
    return apiUrl[shop as keyof typeof apiUrl];
  };
  const lambdaUrl = getLambdaUrl();
  if (!lambdaUrl) return;

  try {
    const response = await fetch(lambdaUrl, {
      method: "POST",
      body: JSON.stringify({
        ...param,
        ...(useTestClientKey && { useTestClientKey: true }),
      }),
    }).then((res) => res);
    const data = (await response.json()) as ICreateDraftOrder;
    return { status: response.status, data };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(message);
  }
};
